import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { ChevronRight } from "lucide-react";
import { Helmet } from "react-helmet";
import { translations } from "@/translations/translations";

const routeMapping: Record<string, { en: string; de: string }> = {
    "blog": { en: "Blog", de: "Blog" },
    "perspectives": { en: "Perspectives", de: "Perspektiven" },
    "consulting": { en: "Consulting", de: "Beratung" },
    "impressum": { en: "Legal Notice", de: "Impressum" },
    "free-tools": { en: "Free Tools", de: "Kostenlose Tools" },
    "leadership-roles": { en: "Leadership Roles", de: "Führungsrollen" },
    "ai-tool-evaluation-prompt-generator": { en: "AI Tool Evaluation", de: "KI-Tool Bewertung" }
};

const Breadcrumbs = () => {
    const location = useLocation();
    const { t, language } = useLanguage();
    const pathnames = location.pathname.split("/").filter((x) => x);

    if (pathnames.length === 0) return null;

    // Schema.org BreadcrumbList
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://rassner-dev.org/"
            },
            ...pathnames.map((value, index) => {
                const to = `/${pathnames.slice(0, index + 1).join("/")}`;
                const mapping = routeMapping[value];
                const displayName = mapping
                    ? mapping[language as 'en' | 'de']
                    : value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');

                return {
                    "@type": "ListItem",
                    "position": index + 2,
                    "name": displayName,
                    "item": `https://rassner-dev.org${to}`
                };
            })
        ]
    };

    const safeJsonLdStringify = (data: unknown) => {
        return JSON.stringify(data).replace(/</g, '\\u003c');
    };

    return (
        <nav aria-label="breadcrumb" className="mb-4 animate-fade-in">
            <Helmet>
                <script type="application/ld+json">
                    {safeJsonLdStringify(breadcrumbSchema)}
                </script>
            </Helmet>
            <ol className="flex items-center space-x-2 text-sm text-muted-foreground flex-wrap">
                <li>
                    <Link to="/" className="hover:text-primary transition-colors">
                        {t('common.home')}
                    </Link>
                </li>
                {pathnames.map((value, index) => {
                    const to = `/${pathnames.slice(0, index + 1).join("/")}`;
                    const isLast = index === pathnames.length - 1;

                    const mapping = routeMapping[value];
                    // If it's the last item and it's a blog post (long slug), we might want to truncate or keep it as is.
                    // For now, doing standard formatting if not mapped.
                    const displayName = mapping
                        ? mapping[language as 'en' | 'de']
                        : value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');

                    // Heuristic: If it looks like a blog slug (long with dashes), maybe we just show "Post" or keep it?
                    // User Request: "Home > Blog > [Post Title]". 
                    // Since we don't have the post title in the URL params easily without fetching, 
                    // we will rely on the page itself to render Breadcrumbs if we want dynamic titles passed in, 
                    // OR we just use the slug for now which is "okay". 
                    // BUT, actually, for blog posts, `value` is the slug. 
                    // Ideally we'd want the actual title. 

                    // IMPROVEMENT: If the component is used inside a page that knows the title, maybe we can pass it as a prop?
                    // But for now let's stick to the URL based approach or simple slug formatting.
                    // The user said "also make it available when being on a blog article!".
                    // Standard slug formatting `some-post-title` -> `Some Post Title` is usually good enough.

                    return (
                        <li key={to} className="flex items-center space-x-2">
                            <ChevronRight className="h-4 w-4 shrink-0" />
                            {isLast ? (
                                <span className="font-medium text-foreground line-clamp-1 max-w-[200px] sm:max-w-none" title={displayName}>{displayName}</span>
                            ) : (
                                <Link to={to} className="hover:text-primary transition-colors whitespace-nowrap">
                                    {displayName}
                                </Link>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

export default Breadcrumbs;
